import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClientProvider } from "@tanstack/react-query";
import { AdminerProvider } from "@marekl/adminer";
import { createRestAuthAdapter } from "@marekl/adminer/auth-rest-default";
import "@marekl/adminer/styles";
import { setupDayjs } from "@marekl/adminer/datetime";
import { configureFormatting } from "@marekl/adminer/format";
import { configureNotify } from "@marekl/adminer/api";
import { resolveTranslator } from "@marekl/adminer/util";
import cs from "@marekl/adminer/i18n/cs";
import {
  ErrorBoundary,
  ErrorBoundaryFallback,
} from "@marekl/adminer/components";
import { queryClient } from "./lib/queryClient";
import { apiClient } from "./lib/api";
import { LinkComponent } from "./lib/LinkComponent";
import type { User } from "./auth/queries";
import App from "./App";
import "./index.css";

// Invoicing is anchored to Czech civil dates: every date-only value (issue
// date, DUZP, due date) is read in Prague time whatever the browser's zone
// (POC 73ae7df; ADMINER_PROPOSALS A12). UI language is Czech.
setupDayjs({ timezone: "Europe/Prague" });
configureFormatting({
  locale: "cs",
  currency: "CZK",
  // Czech style (ČSN 01 6910), as the legacy app printed it.
  dateFormat: "DD. MM. YYYY",
  dateTimeFormat: "DD. MM. YYYY HH:mm",
  timeFormat: "HH:mm",
});
// Provider-independent translator backed by the lib's Czech dictionary: used
// by notify() and by the root ErrorBoundary fallback, both of which live
// outside <AdminerProvider>.
const t = resolveTranslator(cs);

// Legacy wording where it differs from the library's Czech: the list card's
// "Add filter" trigger was "filtry". The provider applies the module's
// formats over configureFormatting, so the Czech-style dates go here too.
const i18n = {
  ...cs,
  messages: { ...(cs.messages as Record<string, string>), "filterPanel.addFilter": "filtry" },
  formats: {
    ...cs.formats,
    dateFormat: "DD. MM. YYYY",
    dateTimeFormat: "DD. MM. YYYY HH:mm",
    datePlaceholder: "DD. MM. RRRR",
    dateTimePlaceholder: "DD. MM. RRRR HH:MM",
  },
};
configureNotify({ t });

const authAdapter = createRestAuthAdapter<User>({ api: apiClient });

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <ErrorBoundary
      fallback={({ onReload, onGoHome }) => (
        <ErrorBoundaryFallback
          onReload={onReload}
          onGoHome={onGoHome}
          title={t("errorBoundary.title")}
          subtitle={t("errorBoundary.subtitle")}
          alertTitle={t("errorBoundary.alertTitle")}
          alertBody={t("errorBoundary.alertBody")}
          homeLabel={t("errorBoundary.actionHome")}
          reloadLabel={t("errorBoundary.actionReload")}
        />
      )}
    >
      <QueryClientProvider client={queryClient}>
        {/* The router lives inside App (RouterProvider on a data router);
            AdminerProvider itself uses no router hooks — LinkComponent only
            renders react-router Links from within the routed tree. */}
        <AdminerProvider
          auth={authAdapter}
          i18n={i18n}
          LinkComponent={LinkComponent}
          // Every data table in this theme is a framed surface with a sticky
          // header; said once rather than at each call site, where it was
          // already wrong twice. A table inside a Card drops the frame on its
          // own — the card is the frame.
          // stickyHeader is deliberately off: in this shell the scroller is
          // AppShell__main, while a table's wrap is its own scrollport with no
          // bounded height — so the header scrolls away with the page and the
          // only thing the option adds is a band above the header. Reported.
          tableDefaults={{ framed: true, hover: true }}
        >
          <App />
        </AdminerProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  </React.StrictMode>,
);
